import { API } from "@swivl-mono/api-helper";
import { BrandInterface, BrandThemeInterface, DefaultTheme } from "@swivl-mono/types";
import { GroupInterface } from "@swivl-mono/types";
import { Button, Spinner } from "@swivl-mono/ui";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Member } from "../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { loadGoogleSheetNames } from "./Onboarding.GoogleSheetImport";
import { COLUMN_MAPPING, GoogleSheetData } from "./Onboarding.ValidationAndImport";
import { styles } from "./shared/onboarding.styles";
import { extractSheetId } from "./shared/onboarding.utils";
import { ValidationError, validateBoolean, validateCountry, validateURL } from "./shared/validation";

// Constants
const VALID_TIERS = ["Engage", "Foundation", "Innovator", "Disruptor"] as const;
const SKIPPED_VARIABLES = ["Secondary Brand Color"];
const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{3,6}$/;
const DEFAULT_AVATAR_URL = "https://swivl-cdn.s3-us-west-1.amazonaws.com/avatar-default-org.png";

function isUsingDefaultTheme(brand: BrandInterface): boolean {
  const hasCustomAvatar = brand.avatarURL && brand.avatarURL !== DEFAULT_AVATAR_URL;
  return (
    brand.theme?.primaryBackground === DefaultTheme.primaryBackground &&
    brand.theme?.secondaryBackground === DefaultTheme.secondaryBackground &&
    !hasCustomAvatar
  );
}

// Tier-specific vars
const TIER_VARIABLES = {
  innovator: [
    { key: "SubscriptionMaxUsers", value: "10" },
    { key: "AgentAssistEnabled", value: "1" },
    { key: "SubscriptionEscalationSummary", value: "1" },
    { key: "HasReviewManager", value: "1" },
    { key: "HasSMSCampaigns", value: "1" },
    { key: "HasFMSPlus", value: "1" },
    { key: "HasGoogleReviewIntegration", value: "1" },
    { key: "FMSplusPastDueShortlinksEnabled", value: "1" },
    { key: "FMSplusAbandonedCartsShortlinksEnabled", value: "1" }
  ],
  disruptor: [{ key: "swivlHasVoice", value: "1" }]
} as const;

export const normalizeValue = (value: string | undefined): string => {
  if (!value) return "";
  const trimmed = value.trim();
  const lower = trimmed.toLowerCase();

  // Convert booleans to 1/0
  if (lower === "true" || lower === "yes") return "1";
  if (lower === "false" || lower === "no") return "0";

  return trimmed;
};

type TierValue = (typeof VALID_TIERS)[number];
type VariableLevel = "org" | "group";

interface TestResults {
  totalVariables: number;
  groupCount: number;
  variables: Record<string, string>[];
}

export function OrgVariablesView() {
  const [sheetUrl, setSheetUrl] = useState("");
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);
  const [sheetData, setSheetData] = useState<GoogleSheetData | null>(null);
  const [groups, setGroups] = useState<GroupInterface[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [variableLevel, setVariableLevel] = useState<VariableLevel>("org");
  const [isValidating, setIsValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isTestingImport, setIsTestingImport] = useState(false);
  const [testResults, setTestResults] = useState<TestResults | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [variablesImported, setVariablesImported] = useState(false);
  const [isCreatingBrandAndEmbeds, setIsCreatingBrandAndEmbeds] = useState(false);
  const [importedVariables, setImportedVariables] = useState<Record<string, string>[] | null>(null);

  const extractVariableValue = useCallback((variables: Record<string, string>[], variableName: string): string => {
    const record = variables.find((record) => record["Variable Name"]?.trim() === variableName);
    return record ? normalizeValue(record["Value (leave blank if it does not apply)"]) : "";
  }, []);

  const createValidationError = useCallback(
    (_variableName: string, value: string, error: string, rowIndex: number): ValidationError => ({
      field: "Variable Name",
      value,
      error,
      rowIndex,
      colIndex: 2
    }),
    []
  );

  const validateOrgVariable = useCallback(
    (record: Record<string, string>, rowIndex: number): ValidationError[] => {
      const errors: ValidationError[] = [];
      const variableName = record["Variable Name"]?.trim();
      const value = record["Value (leave blank if it does not apply)"]?.trim();

      if (!variableName || !value) return errors;

      // Tier validation
      if (variableName === "Tier" && !VALID_TIERS.includes(value as TierValue)) {
        errors.push(createValidationError(variableName, value, "Tier must be Engage, Foundation, Innovator, or Disruptor", rowIndex));
      }

      // URL validation
      if (variableName.includes("URL")) {
        const urlResult = validateURL(value);
        if (!urlResult.isValid) {
          errors.push(createValidationError(variableName, value, urlResult.error!, rowIndex));
        }
      }

      // Brand color validation
      if (variableName.includes("Brand Color") && !HEX_COLOR_PATTERN.test(value)) {
        errors.push(createValidationError(variableName, value, "Brand colors must be in hex format (#000000 or #000)", rowIndex));
      }

      // Country code validation
      if (variableName === "Country Code") {
        const countryResult = validateCountry(value);
        if (!countryResult.isValid) {
          errors.push(createValidationError(variableName, value, countryResult.error!, rowIndex));
        }
      }

      // Boolean validation for rows 26+
      if (rowIndex >= 26) {
        const booleanResult = validateBoolean(value);
        if (!booleanResult.isValid) {
          errors.push(createValidationError(variableName, value, booleanResult.error!, rowIndex));
        }
      }

      return errors;
    },
    [createValidationError]
  );

  useEffect(() => {
    if (sheetUrl.trim()) {
      try {
        const sheetId = extractSheetId(sheetUrl);
        loadGoogleSheetNames(sheetId, setIsLoadingSheets, setAvailableSheets, setSelectedSheet);
      } catch (error) {
        setAvailableSheets([]);
        setSelectedSheet("");
      }
    } else {
      setAvailableSheets([]);
      setSelectedSheet("");
    }
  }, [sheetUrl]);

  const handleValidateData = useCallback(async () => {
    if (!sheetUrl.trim()) return;

    setIsValidating(true);
    setSheetData(null);
    setTestResults(null);
    setValidationErrors([]);

    try {
      const sheetId = extractSheetId(sheetUrl);
      const range = selectedSheet ? `${selectedSheet}!A:C` : "A:C";
      const response = await API.Google.readGoogleSheet(sheetId, range);

      const allErrors = response.records.flatMap((record, index) => validateOrgVariable(record, index + 2));

      setValidationErrors(allErrors);
      setSheetData(response);
    } catch (error) {
      AlertsModel.errorToast(`Failed to validate sheet: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsValidating(false);
    }
  }, [sheetUrl, selectedSheet, validateOrgVariable]);

  const handleTestImport = useCallback(async () => {
    if (!sheetData) return;

    setIsTestingImport(true);
    setValidationErrors([]);
    try {
      const orgGroups = await API.Locations.getGroupsAndLocations(Member.orgId);
      setGroups(orgGroups || []);

      // Auto-select org level for single group
      if (orgGroups.length === 1) {
        setVariableLevel("org");
      }

      const validVariables = sheetData.records.filter(
        (record) => record["Variable Name"]?.trim() && record["Value (leave blank if it does not apply)"]?.trim()
      );

      setTestResults({
        totalVariables: validVariables.length,
        groupCount: orgGroups.length,
        variables: validVariables
      });
    } catch (error) {
      AlertsModel.errorToast(`Test failed: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsTestingImport(false);
    }
  }, [sheetData]);

  const createVariable = useCallback(
    async (key: string, value: string): Promise<void> => {
      await API.LocationVars.createVariable(
        key,
        value,
        Member.orgId,
        variableLevel === "group" ? selectedGroupId || undefined : undefined,
        undefined // locationId
      );
    },
    [variableLevel, selectedGroupId]
  );

  const createTierVariables = useCallback(
    async (tierValue: string): Promise<number> => {
      let created = 0;

      if (tierValue === "Innovator" || tierValue === "Disruptor") {
        // Create Innovator variables
        await Promise.all(TIER_VARIABLES.innovator.map(({ key, value }) => createVariable(key, value)));
        created += TIER_VARIABLES.innovator.length;

        // Create Disruptor-specific variables
        if (tierValue === "Disruptor") {
          await Promise.all(TIER_VARIABLES.disruptor.map(({ key, value }) => createVariable(key, value)));
          created += TIER_VARIABLES.disruptor.length;
        }
      }

      return created;
    },
    [createVariable]
  );

  // add a brand
  const createBrand = useCallback(
    async (variables: Record<string, string>[]): Promise<BrandInterface> => {
      const brandName = extractVariableValue(variables, "Location Name") || "Default Brand";
      const primaryColor = extractVariableValue(variables, "Primary Brand Color") || DefaultTheme.primaryBackground;
      const secondaryColor = extractVariableValue(variables, "Secondary Brand Color") || DefaultTheme.secondaryBackground;
      const logoURL = extractVariableValue(variables, "Photo URL") || undefined;

      const theme: Partial<BrandThemeInterface> = {
        ...DefaultTheme,
        primaryBackground: primaryColor,
        primaryHover: secondaryColor,
        primaryActive: primaryColor,
        secondaryBackground: secondaryColor,
        secondaryText: "#FFFFFF",
        secondaryHover: primaryColor,
        secondaryActive: secondaryColor
      };

      // Generate base slug from brand name
      const baseSpotSlug =
        brandName
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-")
          .replace(/^-|-$/g, "") || "default";

      const baseSpotLinkSlug =
        brandName
          .split(/[\s-]+/)
          .map((word) => word.replace(/[^a-z0-9]/gi, "").toLowerCase())
          .filter((word) => word.length > 0)
          .map((word) => word.substring(0, 3))
          .join("")
          .substring(0, 10) || "def";

      // Ensure uniqueness by adding timestamp suffix (last 3 digits)
      const timestamp = Date.now().toString().slice(-3);
      const spotSlug = `${baseSpotSlug}-${timestamp}`;
      const spotLinkSlug = `${baseSpotLinkSlug}${timestamp}`;

      // Check for an existing default brand
      const existingBrands = await API.Brands.getBrands(Member.orgId);
      const existingDefault = existingBrands?.find((b) => b.isDefault);

      let resolvedBrand: BrandInterface;

      const updatedFields = { name: brandName, theme: theme as BrandThemeInterface, avatarURL: logoURL, spotSlug, spotLinkSlug };

      if (existingDefault && isUsingDefaultTheme(existingDefault)) {
        // Existing default is uncustomized — update it in place
        await API.Brands.updateBrand(Member.orgId, { ...existingDefault, ...updatedFields });
        resolvedBrand = { ...existingDefault, ...updatedFields };
      } else {
        // No existing default, or existing default has been customized — create a new one
        resolvedBrand = await API.Brands.createBrand(Member.orgId, { ...updatedFields, isDefault: true });

        // Unset the previous default if it was customized
        if (existingDefault) {
          await API.Brands.updateBrand(Member.orgId, { ...existingDefault, isDefault: false });
        }
      }

      try {
        await API.LocationVars.createVariable(
          "SpotBrandSlug",
          spotSlug,
          Member.orgId,
          variableLevel === "group" ? selectedGroupId || undefined : undefined,
          undefined
        );
      } catch (error) {
        console.warn("Failed to create SpotBrandSlug variable:", error);
      }

      return resolvedBrand;
    },
    [extractVariableValue, variableLevel, selectedGroupId]
  );

  // Create chatbots/embeds
  const createEmbeds = useCallback(
    async (variables: Record<string, string>[], createdBrand: any): Promise<void> => {
      try {
        if (!createdBrand) {
          console.warn("No brand provided for embed creation");
          return;
        }

        const embedName = extractVariableValue(variables, "Location Name") || "Default Chatbot";
        const preloadVariables: Record<string, string> = {};
        if (variableLevel === "group" && selectedGroupId) {
          preloadVariables["User:DefaultGroupId"] = selectedGroupId.toString();
        }

        const baseSettings = {
          ctaBody: "You can ask us questions about inventory, pricing, amenities and more!",
          dotText: "Questions? How can we help?",
          showCTA: true,
          ctaTitle: "How can we help?",
          position: "bottom-right",
          avatarURL: undefined,
          headerText: `Chat with <b>${embedName}</b>`,
          ctaImageURL: "https://swivl-cdn.s3-us-west-1.amazonaws.com/compass-cta.jpg",
          collapsedStyle: "dot",
          startOpen: false,
          loadPastMessages: true,
          showAvatar: true,
          showAvatarInHeader: true,
          showAvatarWithTimestamp: false,
          language: "en"
        };

        // Create Desktop embed
        const desktopSettings = {
          ...baseSettings,
          ctaDelay: 20,
          ctaStartOnQuest: "PrqfBDIR1Q"
        };

        const desktopEmbedData = {
          name: `${embedName} - Desktop`,
          settings: desktopSettings,
          brandId: createdBrand.id,
          preloadVariables: preloadVariables
        };

        const desktopEmbed = await API.Embeds.createEmbed(Member.orgId, desktopEmbedData);

        // Create Mobile embed
        const mobileSettings = {
          ...baseSettings,
          ctaDelay: 30,
          ctaStartOnQuest: "CyhfOAAf37"
        };

        const mobileEmbedData = {
          name: `${embedName} - Mobile`,
          settings: mobileSettings,
          brandId: createdBrand.id,
          preloadVariables: preloadVariables
        };

        const mobileEmbed = await API.Embeds.createEmbed(Member.orgId, mobileEmbedData);
      } catch (error) {
        console.error("Error creating embeds:", error);
        AlertsModel.errorToast("Error creating chatbot embeds");
      }
    },
    [variableLevel, selectedGroupId, extractVariableValue]
  );

  const handleImportVariables = useCallback(async () => {
    if (!testResults || !Member.orgId) return;

    setIsImporting(true);
    try {
      let created = 0;

      // Create main variables
      for (const record of testResults.variables) {
        const sheetVariableName = record["Variable Name"]?.trim();
        const value = record["Value (leave blank if it does not apply)"]?.trim();

        if (sheetVariableName && value && !SKIPPED_VARIABLES.includes(sheetVariableName)) {
          const actualVariableName = COLUMN_MAPPING[sheetVariableName] || sheetVariableName;
          const finalValue = normalizeValue(value);

          await createVariable(actualVariableName, finalValue);
          created++;
        }
      }

      // Create tier-specific variables
      const tierRecord = testResults.variables.find((record) => record["Variable Name"]?.trim() === "Tier");

      if (tierRecord) {
        const tierValue = normalizeValue(tierRecord["Value (leave blank if it does not apply)"]).trim();
        created += await createTierVariables(tierValue);
      }

      setVariablesImported(true);
      setImportedVariables(testResults.variables);
      setSheetData(null);
      setTestResults(null);
    } catch (error) {
      AlertsModel.errorToast(`Failed to import variables: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsImporting(false);
    }
  }, [testResults, createVariable, createTierVariables]);

  // Handle creating brand and embeds after variables are imported
  const handleCreateBrandAndEmbeds = useCallback(async () => {
    if (!importedVariables) return;

    setIsCreatingBrandAndEmbeds(true);
    try {
      const createdBrand = await createBrand(importedVariables);
      await createEmbeds(importedVariables, createdBrand);

      AlertsModel.successToast("Successfully created brand and chatbots!");
      setVariablesImported(false); // Reset state
      setImportedVariables(null);
    } catch (error) {
      AlertsModel.errorToast(`Failed to create brand and embeds: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsCreatingBrandAndEmbeds(false);
    }
  }, [importedVariables, createBrand, createEmbeds]);

  const canValidate = useMemo(
    () => sheetUrl.trim() && !(availableSheets.length > 1 && !selectedSheet) && !isLoadingSheets,
    [sheetUrl, availableSheets.length, selectedSheet, isLoadingSheets]
  );

  const canImport = useMemo(
    () => !isImporting && !(variableLevel === "group" && groups.length > 1 && !selectedGroupId),
    [isImporting, variableLevel, groups.length, selectedGroupId]
  );

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>2️⃣ Import Org Info & Create Brand + Chatbots</h2>
      <p style={styles.description}>
        Import org-level info like URLs, phone, and email from the 'Org Info' tab of the Google Sheet. Settings will be enabled based on the tier
        selected, and you'll be given the option to create a brand and chatbots as well.
      </p>

      <div style={styles.requirementsBox}>
        <h4 style={styles.heading}>Requirements:</h4>
        <ol style={styles.description}>
          <li>🧑‍💻 URLs have been tested and are working</li>
          <li>💸 Tier is selected</li>
          <li>🎨 Brand colors are defined in hex format (#000000)</li>
        </ol>
      </div>

      <div style={styles.inputWrapper}>
        <input
          type="url"
          value={sheetUrl}
          onChange={(e) => setSheetUrl(e.target.value)}
          placeholder="Google Sheet URL: https://docs.google.com/spreadsheets/d/..."
          style={styles.input}
        />
      </div>

      {availableSheets.length > 1 && (
        <div style={styles.inputWrapper}>
          <select value={selectedSheet} onChange={(e) => setSelectedSheet(e.target.value)} disabled={isLoadingSheets} style={styles.input}>
            <option value="">Select a sheet...</option>
            {availableSheets.map((sheet) => (
              <option key={sheet} value={sheet}>
                {sheet}
              </option>
            ))}
          </select>
        </div>
      )}

      {isLoadingSheets && <div style={styles.loadingText}>⏳ Loading sheets...</div>}

      <div style={styles.buttonGroup}>
        <Button onClick={handleValidateData} disabled={isValidating || !canValidate}>
          {isValidating ? (
            <>
              <Spinner /> Validating...
            </>
          ) : (
            "Validate Data"
          )}
        </Button>

        <Button onClick={handleTestImport} disabled={!sheetData || isTestingImport}>
          {isTestingImport ? (
            <>
              <Spinner /> Testing...
            </>
          ) : (
            "Test Import"
          )}
        </Button>

        {testResults && (
          <Button onClick={handleImportVariables} disabled={!canImport}>
            {isImporting ? (
              <>
                <Spinner /> Importing...
              </>
            ) : (
              "Import"
            )}
          </Button>
        )}
      </div>

      {sheetData && validationErrors.length === 0 && (
        <div style={styles.successBox}>
          <h3 style={styles.successHeading}>✅ Validation Passed</h3>
          <div style={{ fontSize: "14px", color: "#374151" }}>
            <p>{sheetData.records.length} variables validated, 0 errors</p>
          </div>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div style={styles.errorBox}>
          <h3 style={styles.errorHeading}>❌ Validation Failed</h3>
          <p style={{ fontSize: "14px", marginBottom: "10px" }}>Found {validationErrors.length} error(s). Please fix these before proceeding:</p>
          <div style={{ maxHeight: "150px", overflowY: "auto", fontSize: "12px" }}>
            {validationErrors.map((error, i) => (
              <div key={i} style={{ marginBottom: "5px" }}>
                <strong>Row {error.rowIndex}:</strong> {error.error} (Value: "{error.value}")
              </div>
            ))}
          </div>
        </div>
      )}

      {testResults && (
        <div style={{ ...styles.successBox, marginTop: "20px" }}>
          <h3 style={styles.successHeading}>🧪 Test Mode Results</h3>
          <p style={{ fontSize: "14px", marginBottom: "10px" }}>
            <strong>{testResults.totalVariables}</strong> variables ready to import
            {testResults.groupCount === 1 ? " (Org-level)" : variableLevel === "org" ? " (Org-level)" : " (Group-level)"}
          </p>

          {/* Group Selection Logic */}
          {testResults.groupCount > 1 && (
            <div style={{ marginBottom: "15px" }}>
              <div style={{ marginBottom: "10px" }}>
                <label>
                  <input
                    type="radio"
                    value="org"
                    checked={variableLevel === "org"}
                    onChange={(e) => setVariableLevel(e.target.value as VariableLevel)}
                    style={{ marginRight: "8px" }}
                  />
                  Create org-level variables
                </label>
              </div>
              <div style={{ marginBottom: "10px" }}>
                <label>
                  <input
                    type="radio"
                    value="group"
                    checked={variableLevel === "group"}
                    onChange={(e) => setVariableLevel(e.target.value as VariableLevel)}
                    style={{ marginRight: "8px" }}
                  />
                  Create group-level variables for:
                </label>
                {variableLevel === "group" && (
                  <select
                    value={selectedGroupId || ""}
                    onChange={(e) => setSelectedGroupId(Number(e.target.value) || null)}
                    style={{ ...styles.input, marginLeft: "10px" }}
                  >
                    <option value="">Select a group...</option>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}

          <details style={{ fontSize: "13px", marginTop: "10px" }}>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>View variables ({testResults.totalVariables})</summary>
            <div style={{ maxHeight: "150px", overflowY: "auto", marginTop: "5px", fontSize: "12px" }}>
              {testResults.variables.map((variable, i) => (
                <div key={i}>
                  {variable["Variable Name"]} = {variable["Value (leave blank if it does not apply)"]}
                </div>
              ))}
            </div>
          </details>
        </div>
      )}

      {variablesImported && (
        <div style={{ ...styles.successBox, marginTop: "20px" }}>
          <h3 style={styles.successHeading}>✅ Variables Imported Successfully!</h3>
          <p style={{ fontSize: "14px", marginBottom: "20px" }}>
            Your org variables have been imported. Next, create a brand and chatbots to complete the setup.
          </p>
          <Button onClick={handleCreateBrandAndEmbeds} disabled={isCreatingBrandAndEmbeds}>
            {isCreatingBrandAndEmbeds ? (
              <>
                <Spinner /> Creating...
              </>
            ) : (
              "Create Brand & Chatbots"
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
